// src/ariaCapabilities.js
// ═══════════════════════════════════════════════════════════════════════════
// CANONICAL LIST — every visual/computer-output thing Aria can do
// in the CONSUMER app (HowTo). Business-management tools (CRM, payroll,
// dispatch, invoicing, leads, etc.) live in Aria-admin, not here.
// ═══════════════════════════════════════════════════════════════════════════
//
// One source of truth used by:
//   - ExploreView.jsx (renders the categorized grid)
//   - Aria herself (so "what can you do?" returns the same list)
//   - Workbench routing (which surface a tile opens)
//
// Schema:
//   {
//     id:          unique slug, kebab-case
//     title:       what the user sees on the tile — short noun phrase
//                  (the category supplies the verb)
//     category:    one of CATEGORY_ORDER below
//     emoji:       single emoji icon
//     description: one-line "what Aria produces" — variants listed inline
//     route:       '#hash' | 'wb-<name>.html' | null (prefill home input)
//     status:      'live' | 'beta' | 'coming'
//     prompt:      fallback prefill text if route is null
//   }
//
// Consolidation rules:
//   1. If two tiles share the same workbench AND prompt-shape, merge them.
//   2. Tiles must be useful to a regular consumer — not a business owner.
//      (Personal taxes ✓; payroll ✗. Roommate agreement ✓; SOP ✗.)
//
// THE USER-MODELING PRINCIPLE (per owner, 2026-06-01) — foundation layer:
//   Every other rule depends on this one. Aria models the user's
//   personality FROM THE FIRST WORD via observation — type style, pace,
//   decision style, corrections, repeats, gaps, taste. By the 3rd
//   conversation she should feel like an assistant who knows them.
//   Without this model she can't tune to a receiver, can't pick visuals
//   that pop, can't bridge senses. Infra: services/userProfiler.js, 22
//   signal collectors, personality_profile column on user tables,
//   ariaLongTermMemory.js for persistence.
//
// THE RECEIVER-FIRST PRINCIPLE (per owner, 2026-06-01, revised 06-02):
//   OBSERVED, not asked. Aria designs for the RECEIVER, not the maker —
//   but she figures out who that receiver is by WATCHING the user work.
//   The moment a message comes in she's analyzing in the background:
//   who is the user trying to reach, what are they trying to do? She
//   reads their input, uploads, references, edits, and rejections, and
//   builds a confidence score quietly.
//
//   She does NOT pop a "who is this for?" modal. She does NOT interview.
//   She lets the user PLAY with it until she has an idea. While she's
//   still gathering signal, she just helps generically and competently.
//
//   Once she's CONFIDENT (≥ 60), she OFFERS — never asks:
//   "Looks like this is for your IG feed — want me to tighten it for that
//   crowd?" The user accepts, declines, or corrects. Aria adjusts.
//
//   Workbench surface: a quiet "Designed for: [inference]" chip on the
//   artifact that the user can edit. Low confidence = chip is faded or
//   absent (no premature commitment).
//
// THE FIVE SENSES PRINCIPLE (per owner, 2026-06-01):
//   Aria interacts with humans through TWO of the five senses — sight +
//   hearing. She can't touch, taste, or smell. (Smell + taste share the
//   same neural pathway.) Her visuals and audio MUST be excellent — they
//   carry weight the senses she lacks would normally carry.
//
//   When a user evaluates something sensory she can't physically test
//   (food, fabric, perfume, wine, a room, a massage), Aria compensates:
//     • High-fidelity visuals — real photos, renders, NOT clip-art
//     • Bridging language — taste-via-color, fabric-via-flow, smell-via-mood
//     • Audio when relevant — ambient sound, music pairings
//
//   Every workbench artifact must be visually EXCEPTIONAL. Words alone are
//   weak when the user is making a sensory decision they can't verify.
//
// THE WORKBENCH TEST (per owner, 2026-06-01):
//   A tile gets a workbench ONLY when the user is bringing taste, vision,
//   or spatial sense Aria can't have for them.
//
//   Workbench ✓: image gen (visual imagination), website (aesthetic + product
//     vision), shopping (you see options, pick by taste), remodel (spatial
//     vision), outfit (style preference), wedding (vibe), resume (career
//     narrative + visual layout), tattoo (concept + placement).
//
//   NO workbench (Aria just does it, conversational): research, fact-check,
//     summaries, decisions, replies, scheduling, calendar, news, tracking,
//     habit accountability, tutoring sessions, language practice, taxes
//     (she files), interview prep, negotiation prep, hard-conversation coaching.
//
//   The test: if the user closes their eyes and describes what they want
//   in words, no workbench. If they need to point at something, see options,
//   or react to a visible artifact, workbench needed.

// Order = real consumer AI trends as of 2026, sourced from a16z's
// "Top 100 Gen AI Consumer Apps" (6th edition, March 2026) and current
// usage rankings. NOT vibes — actual market data.
//
//   1. Create  — Image/video gen is #1 by far. Nano Banana drove 10M new
//                users to Gemini in its first week. ChatGPT image, Midjourney,
//                Sora, Veo dominate consumer charts.
//   2. Build   — "Vibe coding" exploded. Cursor at $2B ARR. Lovable, Bolt,
//                v0, Replit Agent all in a16z's top 100. Cracking mainstream.
//   3. Write   — ChatGPT crossed 900M weekly active users; long-form writing
//                + emails remain the universal everyday use.
//   4. Learn   — Students = huge segment. Self-improvement bucket: courses,
//                study help, languages, money smarts, hobby skills.
//   5. Plan    — Horizontal agents (Manus, Genspark) emerging for end-to-end
//                planning tasks; trip + meal planning are popular but smaller.
//   6. Shop    — Major emerging category: Perplexity Shopping, Amazon Rufus,
//                ChatGPT Shopping, Google Shopping AI. Conversational commerce
//                + AI gift discovery + comparison + style. Big consumer pull.
//
// Money category was collapsed into Learn — "Budget" is really financial
// literacy, which fits better as a learn-this-skill tile than as its own row.
export const CATEGORY_ORDER = [
  { id: 'create',   label: 'Create',          tagline: 'Images, video, audio, design' },
  { id: 'build',    label: 'Build',           tagline: 'Sites, apps, code, automations' },
  { id: 'write',    label: 'Write',           tagline: 'Stories, emails, resumes, agreements' },
  { id: 'learn',    label: 'Learn',           tagline: 'Courses, study help, languages, money, skills' },
  { id: 'plan',     label: 'Plan',            tagline: 'Trips, events, workouts, meals' },
  { id: 'shop',     label: 'Shop',            tagline: 'Deals, gifts, lists, comparisons, style' },
];

export const CAPABILITIES = [
  // ── CREATE — visual + audio outputs ──────────────────────────────────────
  { id: 'generate-media',   title: 'Image & Video',          category: 'create',   emoji: '🎨',
    description: 'AI image, video, or animated GIF from your prompt', route: '#imagegen',
    status: 'live', prompt: 'Make me an image of ' },
  { id: 'edit-photo',       title: 'Edit',                   category: 'create',   emoji: '🖼️',
    description: 'Touch up, remove background, restyle, upscale',
    status: 'live', prompt: 'Edit this photo to ' },
  { id: 'design-graphic',   title: 'Motion',                 category: 'create',   emoji: '🎬',
    description: 'Animated motion graphic, intro, transition, kinetic type',
    status: 'live', prompt: 'Make me a motion graphic for ' },
  { id: 'make-music',       title: 'Music',                  category: 'create',   emoji: '🎵',
    description: 'Original song, instrumental track, or beat in any style',
    status: 'live', prompt: 'Make me a song about ' },

  // ── WRITE — text outputs ────────────────────────────────────────────────
  { id: 'write-text',       title: 'Essay or story',         category: 'write',    emoji: '📝',
    description: 'Essay, blog, script, story, poem, lyrics, post',
    status: 'live', prompt: 'Write me ' },
  { id: 'write-message',    title: 'Email or letter',        category: 'write',    emoji: '✉️',
    description: 'Email, letter, apology, recommendation, thank-you',
    status: 'live', prompt: 'Write me an email to ' },
  { id: 'write-resume',     title: 'Resume',                 category: 'write',    emoji: '📄',
    description: 'Job-targeted resume + cover letter',
    status: 'live', prompt: 'Write me a resume for ' },
  { id: 'write-agreement',  title: 'Agreement',              category: 'write',    emoji: '📋',
    description: 'Roommate, freelance, NDA, lease — fillable',
    status: 'live', prompt: 'Write me an agreement for ' },
  { id: 'write-pitch',      title: 'Pitch',                  category: 'write',    emoji: '🚀',
    description: 'Elevator pitch, sales pitch, investor 1-pager, deck script',
    status: 'live', prompt: 'Write me a pitch for ' },

  // ── BUILD — make a thing (digital or physical) ──────────────────────────
  { id: 'build-website',    title: 'Website',                category: 'build',    emoji: '🌐',
    description: 'Personal site, landing page, portfolio, blog', route: '#website',
    status: 'live', prompt: 'Build me a website for ' },
  { id: 'build-app',        title: 'App',                    category: 'build',    emoji: '📱',
    description: 'Web app, mobile app, or browser extension',
    status: 'live', prompt: 'Build me an app that ' },
  { id: 'build-video-game', title: 'Video game',             category: 'build',    emoji: '🎮',
    description: 'Browser or mobile game from your concept',
    status: 'live', prompt: 'Build me a video game where ' },
  { id: 'build-business',   title: 'Business',               category: 'build',    emoji: '💼',
    description: 'Side hustle in a box — name, brand, plan, launch steps',
    status: 'live', prompt: 'Help me start a business for ' },
  { id: 'build-agent',      title: 'Agent',                  category: 'build',    emoji: '🤖',
    description: 'Autonomous AI agent — runs tasks for you end-to-end',
    status: 'live', prompt: 'Build me an agent that ' },
  { id: 'build-blueprint',  title: 'Blueprint',              category: 'build',    emoji: '📐',
    description: 'Floor plan, home design, or layout — visualized + measured',
    status: 'live', prompt: 'Design me a floor plan for ' },
  { id: 'build-remodel',    title: 'Remodel',                category: 'build',    emoji: '🔨',
    description: 'Renovation plan — visual mockup, materials, cost, steps',
    status: 'live', prompt: 'Help me remodel my ' },

  // ── NOTE: "Reach" used to live here (send-message, phone-call, social-post).
  //    Removed for the same reason as the Help category — these aren't
  //    things the user discovers in a grid. They say "text mom I'll be late"
  //    or "call the dentist" or "post this to IG" and Aria does it. The
  //    workbench routes (wb-sms / wb-call / wb-social) still exist and the
  //    intent handlers still fire on the backend.

  // ── PLAN ────────────────────────────────────────────────────────────────
  { id: 'plan-trip',        title: 'Trip',                   category: 'plan',     emoji: '✈️',
    description: 'Full itinerary with booking links',
    status: 'live', prompt: 'Plan a trip to ' },
  { id: 'plan-event',       title: 'Event',                  category: 'plan',     emoji: '🎉',
    description: 'Wedding, birthday, party, get-together',
    status: 'live', prompt: 'Plan an event for ' },
  { id: 'plan-project',     title: 'Project',                category: 'plan',     emoji: '🗺️',
    description: 'Personal project roadmap with milestones',
    status: 'live', prompt: 'Plan a project to ' },
  { id: 'plan-workout',     title: 'Workout',                category: 'plan',     emoji: '💪',
    description: 'Weekly plan with sets, reps, progression',
    status: 'live', prompt: 'Plan a workout for ' },
  { id: 'plan-meals',       title: 'Meals',                  category: 'plan',     emoji: '🍽️',
    description: 'Meal plan + shopping list + recipes',
    status: 'live', prompt: 'Plan meals for ' },

  // ── SHOP — find, compare, gift, list, style. Big consumer AI category in 2026
  //    (Perplexity Shopping, Amazon Rufus, ChatGPT Shopping, Google Shopping AI).
  //    Backend workbench at wb-shopping.html.
  { id: 'shop-find-deals',  title: 'Find deals',             category: 'shop',     emoji: '🛍️',
    description: 'Compare prices, find the best deal, surface coupons + discounts', route: 'wb-shopping.html',
    status: 'live', prompt: 'Find me the best deal on ' },
  { id: 'shop-gift',        title: 'Gift ideas',             category: 'shop',     emoji: '🎁',
    description: 'Personalized gift ideas by person, budget, occasion', route: 'wb-shopping.html',
    status: 'live', prompt: 'Help me find a gift for ' },
  { id: 'shop-list',        title: 'Shopping list',          category: 'shop',     emoji: '📝',
    description: 'Build a shopping list — groceries, gear, or anything else', route: 'wb-shopping.html',
    status: 'live', prompt: 'Build me a shopping list for ' },
  { id: 'shop-style',       title: 'Style or outfit',        category: 'shop',     emoji: '👗',
    description: 'Outfit ideas, fits, lookbook — by body type, budget, occasion',
    status: 'live', prompt: 'Help me put together an outfit for ' },

  // ── LEARN — self-improvement, courses, languages, money smarts, skills ──
  { id: 'make-tutorial',    title: 'Tutorial or course',     category: 'learn',    emoji: '🎓',
    description: 'Step-by-step guide or full course on anything', route: null,
    status: 'coming', prompt: 'Make me a tutorial for ' },
  { id: 'make-study-materials', title: 'Study materials',    category: 'learn',    emoji: '📚',
    description: 'Flashcards, quizzes, study guides, summaries', route: null,
    status: 'coming', prompt: 'Make me study materials for ' },
  { id: 'learn-language',   title: 'Language',               category: 'learn',    emoji: '🗣️',
    description: 'Learn a new language — vocab, grammar, conversation practice', route: null,
    status: 'coming', prompt: 'Teach me ' },
  { id: 'learn-budget',     title: 'Budget',                 category: 'learn',    emoji: '💰',
    description: 'Personal finance — budgeting, saving, investing basics',
    status: 'live', prompt: 'Teach me how to budget ' },
  { id: 'learn-skill',      title: 'Skill',                  category: 'learn',    emoji: '🎯',
    description: 'Learn a hobby — cooking, drawing, music, photography', route: null,
    status: 'coming', prompt: 'Teach me how to ' },
  { id: 'learn-jokes',      title: 'Jokes',                  category: 'learn',    emoji: '🎭',
    description: 'Comedy + joke writing — structure, timing, delivery, standup', route: null,
    status: 'coming', prompt: 'Teach me how to be funny — ' },

  // ── REMOVED — backend / conversational, no browsable output ────────────
  //   Rule: tiles only for things where the OUTPUT is the deliverable
  //   (an image, doc, app, plan). If the user wants Aria's ACTION rather
  //   than a tangible artifact, it doesn't belong in the Explore catalog.
  //
  //   Categories pulled:
  //     Documents  (invoice, estimate, deck, biz plan, SOP)  — admin docs
  //     People     (CRM, employees, dispatch, reviews,...)   — admin tools
  //     Reach      (message, phone-call, social-post)        — conversational
  //     Analyze    (data, report, comparison)                — Aria just does
  //     Help       (fix, take-over, summarize, scan)         — conversational
  //
  //   Categories folded into Learn:
  //     Money      (budget)  → financial literacy is a "learn this" tile
  //
  //   Categories brought back as discoverable tiles:
  //     Shop       — added as its own row (find deals, gift ideas, lists,
  //                   style). Originally pulled under "Help" but consumer AI
  //                   trends in 2026 make shopping a major standalone niche.
  //
  //   Individual tiles pulled:
  //     teach-app  (Live lesson)  — Aria teaches live, no tangible artifact
  //     file-taxes (Taxes)        — Aria does the filing, no browsable output
  //
  //   Backend handlers + workbench routes for all of these still exist
  //   (#screen, #shop, #website, wb-sms, wb-call, wb-social, wb-reading,
  //   wb-scan, wb-shopping, wb-tax, etc). The user just asks in plain
  //   language and Aria handles it — they're not surfaced as discoverable
  //   tiles because they don't pass the "browsable output" test.
];

// Convenience helpers ────────────────────────────────────────────────────────
export function getByCategory(categoryId) {
  return CAPABILITIES.filter((c) => c.category === categoryId);
}
export function getById(id) {
  return CAPABILITIES.find((c) => c.id === id) || null;
}
export function getLiveCount() {
  return CAPABILITIES.filter((c) => c.status === 'live').length;
}
export function getTotalCount() {
  return CAPABILITIES.length;
}
